import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "./ui/drawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Box } from "./Box";
import { Row } from "./Stack";
import { Button } from "./Button";
import { Text } from "./Text";
import { PauseIconWithShadow } from "./Icons";
import {
  OrientationEnum,
  EventsEnum,
  EventNamesEnum,
  PageNameEnum,
} from "@/constants/constants";
import Image from "next/image";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useEffect, useState } from "react";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { uniqueAppRedirectionDeeplink } from "@/utils/common.helpers";

export interface AppOpenDrawerConfig {
  heading: string;
  subHeading: string;
  text: string;
  image?: string;
  desktopImage?: string;
  button: {
    text: string;
    icon?: React.ReactNode;
    href?: string;
    buttonVariant?:
      | "primary-white"
      | "primary"
      | "secondary"
      | "outlined-white"
      | "transparent";
  };
}

const AppOpenContent = ({
  heading,
  subHeading,
  text,
  image,
  button,
  drawerConfig,
  pageName,
}: AppOpenDrawerConfig & {
  drawerConfig?: AppOpenDrawerConfig;
  pageName?: PageNameEnum;
}) => {
  const { trackEvent } = useTrackingAnalytics();

  const redirectionHandler = () => {
    trackEvent({
      eventName: EventNamesEnum.APP_OPEN_DRAWER_CLICKED,
      properties: {
        ...(pageName && { page_name: pageName }),
      },
    });
  };

  return (
    <div className="px-4 pb-10 pt-2 sm:!p-5">
      <div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
        {image && (
          <>
            {drawerConfig?.image ? (
              <Box position="relative" borderRadius={4} overflow="hidden">
                <Image
                  src={image}
                  alt={heading}
                  className="object-contain"
                  width={400}
                  height={400}
                />
                <div className="absolute left-0 top-0 h-full w-full bg-black/40" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60">
                    <PauseIconWithShadow />
                  </div>
                </div>
              </Box>
            ) : (
              <div className="relative after:absolute after:left-1/2 after:top-[98%] after:h-1 after:w-[90%] after:-translate-x-1/2 after:rounded-full after:bg-stg-grey-2 after:blur-[4px] after:content-['']">
                <Image
                  src={image}
                  alt={heading}
                  className="object-contain"
                  width={130}
                  height={156}
                />
              </div>
            )}
          </>
        )}
        <div className="flex flex-col items-center justify-center sm:mt-12 sm:h-[200px] sm:justify-between sm:gap-6">
          <div className="px-10 pb-3 sm:px-6">
            <Row align="center" justify="center" gap={1}>
              <Text size={4} sizeMd={3} appearance="secondary" weight="bold">
                {subHeading}
              </Text>
              <Text size={5.5} weight="bold" alignment="center">
                {heading}
              </Text>
              <Text size={3} sizeMd={2} appearance="secondary">
                {text}
              </Text>
            </Row>
          </div>
          <Button
            variant={button.buttonVariant}
            href={button.href}
            onClick={redirectionHandler}
            icon={button.icon}
          >
            {button.text}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AppOpenDrawer = ({
  open,
  onDrawerOpenChange,
  drawerConfig,
  pageName,
  forceShow = false,
  forceShowDesktop = false,
}: {
  open: boolean;
  onDrawerOpenChange: (open: boolean) => void;
  drawerConfig?: AppOpenDrawerConfig;
  pageName?: PageNameEnum;
  forceShow?: boolean;
  forceShowDesktop?: boolean;
}) => {
  const isMobile = typeof window !== "undefined" ? window.isMobile : false;
  const [isPortrait, setIsPortrait] = useState(false);
  const { t } = useClientTranslations();
  const { trackEvent } = useTrackingAnalytics();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const { heading, subHeading, text, image, desktopImage, button } =
    drawerConfig ?? {
      heading: t("popups.appOpenDrawer.heading"),
      subHeading: t("popups.appOpenDrawer.subHeading"),
      text: t("popups.appOpenDrawer.text"),
      button: {
        text: t("popups.appOpenDrawer.buttonText"),
        href: uniqueAppRedirectionDeeplink(),
        buttonVariant: "primary",
      },
      image: `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}${"/static/app-download-drawer-image.png"}`,
    };

  // Check and listen for orientation changes
  useEffect(() => {
    const checkOrientation = () => {
      const portrait = screen.orientation.type.includes(
        OrientationEnum.PORTRAIT,
      );
      setIsPortrait(portrait);
    };

    // Check initial orientation
    checkOrientation();

    // Listen for orientation changes
    const handleOrientationChange = () => {
      checkOrientation();
    };

    window.addEventListener(
      EventsEnum.ORIENTATION_CHANGE,
      handleOrientationChange,
    );
    screen.orientation?.addEventListener(
      EventsEnum.CHANGE,
      handleOrientationChange,
    );

    return () => {
      window.removeEventListener(
        EventsEnum.ORIENTATION_CHANGE,
        handleOrientationChange,
      );
      screen.orientation?.removeEventListener(
        EventsEnum.CHANGE,
        handleOrientationChange,
      );
    };
  }, []);

  useEffect(() => {
    if (open) {
      trackEvent({
        eventName: EventNamesEnum.APP_OPEN_DRAWER_OPENED,
        properties: {
          ...(pageName && { page_name: pageName }),
        },
      });
    }
  }, [open]);

  // Show Dialog for desktop OR mobile landscape (when forceShowDesktop is true)
  const isMobileLandscape = isMobile && !isPortrait;
  if ((isDesktop || isMobileLandscape) && forceShowDesktop) {
    return (
      <Dialog open={open} onOpenChange={onDrawerOpenChange}>
        <DialogContent className="sm:!rounded-2xl sm:!pt-0">
          <DialogHeader className="hidden">
            <DialogTitle>Open App to watch</DialogTitle>
          </DialogHeader>
          <AppOpenContent
            heading={heading}
            subHeading={subHeading}
            text={text}
            image={desktopImage || image}
            button={button}
            drawerConfig={drawerConfig}
            pageName={pageName}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Check if drawer should be shown (mobile and portrait)
  const shouldShowDrawer = forceShow && isMobile && isPortrait;

  if (!shouldShowDrawer) {
    return null;
  }

  return (
    <Drawer open={open} onOpenChange={onDrawerOpenChange}>
      <DrawerContent>
        {/* For Error - Drawer header is mandatory, it throws error if not present */}
        <DrawerHeader className="hidden">
          <DrawerTitle>Open App to watch</DrawerTitle>
        </DrawerHeader>
        <AppOpenContent
          heading={heading}
          subHeading={subHeading}
          text={text}
          image={image}
          button={button}
          drawerConfig={drawerConfig}
          pageName={pageName}
        />
      </DrawerContent>
    </Drawer>
  );
};
